import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  InquisitiveStimulusResourceType,
  InquisitiveActivitiesComponentType,
} from 'global/graphql/scalars.type';
import {
  ActivityBuilderDrawerWrapper,
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerContent,
  ActivityBuilderDrawerActions,
  ActivityBuilderDrawerHeaderButton,
} from 'approot/activity-builder/activity-builder-drawer';
import { PrimaryButton } from 'approot/shared/styles/buttons';
import type { SearchStimulusResourcesQuery } from '../../../shared/stimulus/stimulus.graphql.generated';
import StimulusSearch from 'approot/shared/stimulus/search/stimulus-search';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { useMultiViewContext } from 'approot/shared/layout/layout-multi-view';
import {
  useGetInteractiveLessonParams,
  useUpdateComponent,
} from 'approot/activity-builder/activity-builder.hooks';
import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import { useGetContentData } from 'approot/create-stimulus/create-stimulus.hooks';
import {
  COMPONENT_LAYOUT_HORIZONTAL,
  ACTIVITIES_COMPONENT_TYPE_DOCUMENT,
  ACTIVITIES_COMPONENT_TYPE_EBOOK,
  LESSON,
  LESSON_ACTIVITIES,
  ASSESSMENT,
} from 'global/constants';
import { PRIVATE_CREATE_STIMULUS } from 'global/private-routes.constants';
import { getInteractiveContentType } from 'approot/activity-builder/activity-builder.utils';
import type { Pluck } from 'lib/gql/pluck';

const SEARCH_CONTAINER = 'search-container';

type Props = {
  sectionId: string;
  componentId: string;
  sort: string;
  type: InquisitiveActivitiesComponentType;
  stimulusType: InquisitiveStimulusResourceType;
  title?: string;
  canGoBack?: boolean;
  lessonId?: number;
  headerActionButton?: ReactNode;
  renderSubmit?: (stimulus?: StimulusResource) => ReactNode;
  onGoBack?: () => void;
};

type StimulusResource = Pluck<
  SearchStimulusResourcesQuery,
  'searchStimulusResources.stimulusResources'
>;

export const StimulusLibrary = ({
  componentId,
  sectionId,
  type,
  sort,
  title,
  stimulusType,
  canGoBack = true,
  lessonId,
  renderSubmit,
  onGoBack,
  headerActionButton,
}: Props) => {
  const { goBack } = useMultiViewContext();
  const { closeDrawer } = useActivityBuilderContext();
  const contentType = useMemo(() => getInteractiveContentType(), []);
  const {
    years,
    subjects,
    loading: lessonDataLoading,
  } = useGetContentData(
    lessonId || 0,
    contentType === LESSON_ACTIVITIES ? LESSON : ASSESSMENT
  );

  const { showError } = useSnackbar();

  const [selectedStimulus, setSelectedStimulus] = useState<
    StimulusResource | undefined
  >();

  const { updateComponent, loading } = useUpdateComponent({
    onCompleted: () => {
      closeDrawer();
    },
    onError: () => showError(`There was an error updating the ${type}`),
  });

  const handleChoose = useCallback(() => {
    if (selectedStimulus) {
      updateComponent({
        sectionId,
        componentId,
        componentData: {
          type,
          sort,
          properties: {
            stimulusId: selectedStimulus.id,
            showCaptionOnLesson: false,
            layout:
              type === ACTIVITIES_COMPONENT_TYPE_DOCUMENT ||
              type === ACTIVITIES_COMPONENT_TYPE_EBOOK
                ? {
                    orientation: COMPONENT_LAYOUT_HORIZONTAL,
                  }
                : undefined,
          },
        },
      });
    }
  }, [selectedStimulus, componentId, sectionId, sort, type, updateComponent]);

  return (
    <ActivityBuilderDrawerWrapper>
      <ActivityBuilderDrawerHeader
        isBack={canGoBack}
        title={title || stimulusType}
        onClose={canGoBack ? onGoBack || goBack : closeDrawer}
        headerButton={headerActionButton}
      />
      <ActivityBuilderDrawerContent id={SEARCH_CONTAINER}>
        {!lessonDataLoading && (
          <StimulusSearch
            withMediaPreview
            type={stimulusType}
            selectedStimulus={selectedStimulus}
            scrollableContainerId={SEARCH_CONTAINER}
            onStimulusSelected={stimulus => setSelectedStimulus(stimulus)}
            subjects={subjects}
            years={years}
          />
        )}
      </ActivityBuilderDrawerContent>
      <ActivityBuilderDrawerActions>
        {renderSubmit ? (
          renderSubmit(selectedStimulus)
        ) : (
          <PrimaryButton
            data-selector="btn-choose-stimulus"
            type="button"
            loading={loading}
            disabled={!selectedStimulus || loading}
            onClick={handleChoose}
          >
            Choose Stimulus
          </PrimaryButton>
        )}
      </ActivityBuilderDrawerActions>
    </ActivityBuilderDrawerWrapper>
  );
};

export const StimulusLibraryCreate = ({
  type,
  contentId,
  componentId,
  sectionId,
}: {
  type: InquisitiveActivitiesComponentType;
  contentId: number;
  componentId: string;
  sectionId: string;
}) => {
  const navigate = useNavigate();
  const {
    contentId: activitiesContentId,
    slug,
    contentType,
  } = useGetInteractiveLessonParams();

  return (
    <ActivityBuilderDrawerHeaderButton
      label="New"
      ariaLabel={`Create new ${type}`}
      onClick={() =>
        navigate(
          generateStimulusCreateLink({
            lessonType: contentType === LESSON_ACTIVITIES ? LESSON : ASSESSMENT,
            contentId,
            activitiesContentId,
            sectionId,
            componentId,
            type,
            slug,
          })
        )
      }
    />
  );
};

export function generateStimulusCreateLink({
  lessonType,
  contentId,
  activitiesContentId,
  sectionId,
  componentId,
  type,
  slug,
}: {
  lessonType: string;
  contentId: number;
  activitiesContentId: number;
  sectionId: string;
  componentId: string;
  type: InquisitiveActivitiesComponentType;
  slug: string;
}) {
  return `${PRIVATE_CREATE_STIMULUS}?routeSlug=${slug}&lessonType=${lessonType}&id=${contentId}&contentId=${activitiesContentId}&sectionId=${sectionId}&componentId=${componentId}&stimulusType=${type}`;
}
